<template>
  <div class="bd-highlight output-button-area mb-2">
    <ITTenkoStatusMessage
      :messages="downloadButtonNotification"
      :showModalButton="showModalButton"
      @openModal="openModal"
    />
  </div>
  <div class="d-flex justify-content-end bd-highlight mb-3 output-button-area">
    <v-btn
      class="exec-btn"
      :disabled="!downloadable"
      @click="handleClickOutputPdf"
      :style="{
        backgroundColor: downloading ? '#B3B8BB' : '',
      }"
    >
      {{ downloadBtnLabel }}
    </v-btn>
  </div>
</template>

<script>
import Api from "@/services/api/ApiServiceFabrick";
import { useAuth } from "@/services/user/Auth0UserProvider";
import { MANUAL_LINK, PAGE_STRUCTURES_BY_DRIVER } from '@/services/consts'
import ITTenkoStatusMessage from "@/components/DailyReportParts/ITTenkoStatusMessage.vue";
import i18n from '@/lang/i18n';

export default {
  setup() {
    const { loginWithRedirect } = useAuth();
    return {
      loginWithRedirect,
      i18n
    };
  },
  props: {
    operationOfficeId: null,
    deliveryPlans: [],
    options: {},
    showFlash: {
      type: Function,
    },
    isItTenkoReacquisition: {
      type: Boolean,
      default: false,
    },
    pageComposition: {
      type: Object,
    },
    selectedReportLayout: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      downloading: false,
      byDriverValidationMsg: {
        driverLimit:
          i18n.global.t('pages.dailyReports.validation.driverLimit'),
        deliveryPlanLimit:
          i18n.global.t('pages.dailyReports.validation.deliveryPlanLimit'),
      },
      downloadButtonNotification: [],
      failedMessagePrefix: i18n.global.t('pages.dailyReports.tripId'),
      failedMessage: i18n.global.t('pages.dailyReports.errorMessage.getTenko'),
      inQueueRetryMessage: i18n.global.t('pages.dailyReports.errorMessage.doesNotExistTenko'),
      failed: "failed",
      inQueueRetry: "in_queue_retry",
      showModalButton: false
    };
  },
  computed: {
    downloadable() {
      return this.deliveryPlans[0] && !this.downloading;
    },
    downloadBtnLabel() {
      return this.downloading ?
        i18n.global.t('common.downloadingBtn') :
        i18n.global.t('common.downloadBtn');
    },
    validationMsgForByDriver() {
      if (this.pageComposition.key != PAGE_STRUCTURES_BY_DRIVER) {
        return "";
      }
      const driverLimit = 1;
      const deliveryPlanLimit = 2;

      const byDriverDeliveryPlanCount = {};
      for (let i = 0; i < this.deliveryPlans.length; i++) {
        const deliveryPlan = this.deliveryPlans[i];
        const driverId = deliveryPlan.driver?.driver_id ?? deliveryPlan.driver_id;

        byDriverDeliveryPlanCount[driverId] =
          (byDriverDeliveryPlanCount[driverId] || 0) + 1;

        const driverIdsCount = Object.keys(byDriverDeliveryPlanCount).length;
        if (driverIdsCount > driverLimit) {
          return this.byDriverValidationMsg.driverLimit;
        }

        if (byDriverDeliveryPlanCount[driverId] > deliveryPlanLimit) {
          return this.byDriverValidationMsg.deliveryPlanLimit;
        }
      }
      return "";
    },
  },
  methods: {
    handleClickOutputPdf() {
      this.setDownloadButtonNotificationMessage();
      const validationMsg = this.validationMsgForByDriver;
      if (validationMsg) {
        this.showFlash(validationMsg, "error", false);
        return false;
      }

      this.downloading = true;
      const params = this.makeCreateFileRequest();
      Api()
        .createJobStatus(params)
        .then((data) => {
          this.pollingJobStatus(data.id);
        })
        .catch((error) => {
          const res = error.response;
          this.downloading = false;
          if (res?.status) {
            switch (res.status) {
              case 401:
                this.loginWithRedirect({
                  appState: { targetUrl: location.pathname },
                });
                break;
              case 403:
                location.reload();
                break;
              case 422:
            }
          } else {
            this.$rollbar.error(error)
          }
        });
    },
    pollingJobStatus(id) {
      const interval = setInterval(() => {
        Api()
          .getJobStatus(id)
          .then((res) => {
            if (res.status == "completed") {
              this.download(res.file_path);
              clearInterval(interval);
              this.downloading = false;
            }
            if (res.status == "failed") {
              let message = res.output_contents.message
              message += "\n\n"

              const detailMessage = res.output_contents.detail_message;
              if (detailMessage) {
                message += `${detailMessage}\n`
              }
              const details = res.output_contents.details
              if (details) {
                  message += this.convertDetailErrors(details)
              }

              let manualLink = ''
              if (detailMessage || details) {
                manualLink = MANUAL_LINK['DailyReport'] + '#page=4'
              }

              this.showFlash(message, 'error', false, manualLink)

              clearInterval(interval);
              this.downloading = false;
            }
            this.setDownloadButtonNotificationMessage(res);
          });
      }, 3000);
    },
    convertDetailErrors(details) {
      const indent = "  "
      let message = ""

      details.forEach((detail) => {
        if (typeof detail === 'string') {
          message += `${detail}\n`
          return
        }
        Object.keys(detail).forEach((key) => {
          if (key == 'title') {
            message += `${detail[key]}\n`
          } else {
            const localeKey = `pages.dailyReports.errorMessage.detailCategory.${key}`
            const category = i18n.global.t(localeKey)
            if (category) {
              message += `${indent}${category}: ${detail[key]}\n`
            }
          }
        })
      })
      return message
    },
    download(path) {
      Api().fileDownload(path);
    },
    makeCreateFileRequest() {
      let type = "App\\Services\\OutputReports\\Daily\\PdfCreateService";
      if (this.options.outputFormat == "CSV") {
        type = "App\\Services\\OutputReports\\Daily\\CsvCreateService";
      }

      return {
        type: type,
        operation_office_id: this.operationOfficeId,
        settings: {
          page_structure: this.pageComposition.data,
          report_layout: this.selectedReportLayout,
          ...this.options,
        },
        trips: this.deliveryPlans.map(
          (deliveryPlan) => deliveryPlan.delivery_plan_id
        ),
        it_tenko_reacquisition: this.isItTenkoReacquisition,
      };
    },
    setDownloadButtonNotificationMessage(job = null) {
      this.showModalButton = false;
      if (job?.output_contents?.statuses) {
        this.downloadButtonNotification = this.checkFailedStatus(job.output_contents.statuses);
        return this.showModalButton = true;
      }
      if (
        job?.status == "working" &&
        job?.parameter?.tenko_job_enqueue_count > 0
      ) {
        return this.downloadButtonNotification = [i18n.global.t('pages.dailyReports.messages.getTenko')];
      }
      this.downloadButtonNotification = [];
    },
    checkFailedStatus(statuses) {
      let messages = []
      Object.values(statuses).forEach((status, index) => {
        if (status == this.failed) {
          messages.push(this.makeMessages(this.failedMessage, statuses, index))
        }
        if (status == this.inQueueRetry) {
          messages.push(this.makeMessages(this.inQueueRetryMessage, statuses, index))
        }
      })
      return messages
    },
    makeMessages(templateMessage, statuses, index) {
      return (`${this.failedMessagePrefix}${Object.keys(statuses)[index]} ${templateMessage}`);
    },
    openModal() {
      this.$emit("openModal", this.downloadButtonNotification)
    },
  },
  watch: {
    deliveryPlans: {
      handler() {
        this.setDownloadButtonNotificationMessage();
      },
    },
  },
  components: {
    ITTenkoStatusMessage,
  },
};
</script>

<style>
.output-button-area .v-btn--disabled.v-btn--variant-contained {
  background-color: rgb(71, 170, 196) !important;
}
</style>
